import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth, normalizeProduct } from '@/lib/firebase';
import { uploadMedia } from '@/lib/cloudinary'; 
import { signOut } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Edit3, LogOut, Package, X, Save, Home, Image, Video,
  LayoutGrid, ChevronRight, Sliders, Settings, Check, GripVertical,
  Phone, Mail, MapPin, Instagram, Clock, FileText, UploadCloud
} from 'lucide-react';
import ProductImage from '@/components/ProductImage';

export default function AdminDashboard({ user }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // State: Form (variants alanı eklendi)
  const [form, setForm] = useState({
    isim: '', aciklama: '', kategori: '', fiyat: '', resim: '', video: '', ozellikler: '', variants: []
  });
  
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('products');

  // Slider state
  const [heroProductIds, setHeroProductIds] = useState([]);
  const [sliderSaving, setSliderSaving] = useState(false);
  const [sliderSaved, setSliderSaved] = useState(false);

  // Settings state
  const [siteSettings, setSiteSettings] = useState({
    companyName: 'AyzPlastik',
    slogan: 'Kalitenin Adı',
    phone: '05542345454',
    email: 'info@ayzplastics.com',
    address: 'Istanbul, Türkiye',
    instagram: 'https://www.instagram.com/ayzplastik?igsh=MWJ6c2djYTdoeTVhag%3D%3D',
    whatsapp: '905542345454',
    workingHours: 'Pzt-Cmt: 09:00 - 18:00',
    aboutText: '',
    aboutMission: '',
    aboutVision: '',
    catalogUrl: '',
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [catalogUploading, setCatalogUploading] = useState(false);

  // Categories state
  const [categoryImages, setCategoryImages] = useState({});
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryUploadingId, setCategoryUploadingId] = useState(null);

  const fetchProducts = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'urunler'));
      setProducts(snapshot.docs.map((d) => normalizeProduct(d)));
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteConfig = async () => {
    try {
      const heroDoc = await getDoc(doc(db, 'site_config', 'hero_slides'));
      if (heroDoc.exists() && heroDoc.data().productIds) {
        setHeroProductIds(heroDoc.data().productIds);
      }
      const settingsDoc = await getDoc(doc(db, 'site_config', 'settings'));
      if (settingsDoc.exists()) {
        setSiteSettings((prev) => ({ ...prev, ...settingsDoc.data() }));
      }
      const catDoc = await getDoc(doc(db, 'site_config', 'categories'));
      if (catDoc.exists() && catDoc.data().images) {
        setCategoryImages(catDoc.data().images);
      }
    } catch (err) {
      console.log('Site config fetch:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchSiteConfig();
  }, []);

  // --- Multi-Media Upload Handlers ---
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setSaving(true);
    try {
      const uploadPromises = files.map(file => uploadMedia(file, 'image'));
      const uploadedUrls = await Promise.all(uploadPromises);
      const newUrlsString = uploadedUrls.join('\n');
      
      setForm(prev => ({
        ...prev,
        resim: prev.resim ? `${prev.resim}\n${newUrlsString}` : newUrlsString
      }));
    } catch (err) {
      alert('Görsel yükleme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setSaving(true);
    try {
      const uploadPromises = files.map(file => uploadMedia(file, 'video'));
      const uploadedUrls = await Promise.all(uploadPromises);
      const newUrlsString = uploadedUrls.join('\n');
      
      setForm(prev => ({
        ...prev,
        video: prev.video ? `${prev.video}\n${newUrlsString}` : newUrlsString
      }));
    } catch (err) {
      alert('Video yükleme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCatalogUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCatalogUploading(true);
    try {
      const url = await uploadMedia(file, 'auto');
      setSiteSettings(prev => ({ ...prev, catalogUrl: url }));
    } catch (err) {
      alert('Katalog yükleme hatası: ' + err.message);
    } finally {
      setCatalogUploading(false);
    }
  };

  const handleCategoryImgUpload = async (e, categoryName) => {
    const file = e.target.files[0];
    if (!file) return;

    setCategoryUploadingId(categoryName);
    try {
      const url = await uploadMedia(file, 'image');
      const updatedImages = { ...categoryImages, [categoryName]: url };
      setCategoryImages(updatedImages);
      await setDoc(doc(db, 'site_config', 'categories'), { images: updatedImages }, { merge: true });
    } catch (err) {
      alert('Kategori görseli yükleme hatası: ' + err.message);
    } finally {
      setCategoryUploadingId(null);
    }
  };

  // --- Variant Handlers ---
  const addVariant = () => {
    setForm(prev => ({
      ...prev,
      variants: [...prev.variants, { name: '', price: '' }]
    }));
  };

  const removeVariant = (index) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariant = (index, field, value) => {
    setForm(prev => {
      const newVariants = [...prev.variants];
      newVariants[index][field] = value;
      return { ...prev, variants: newVariants };
    });
  };

  // Product CRUD
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const images = form.resim.split('\n').map(s => s.trim()).filter(Boolean);
      const videos = form.video.split('\n').map(s => s.trim()).filter(Boolean);
      const features = form.ozellikler.split('\n').map(s => s.trim()).filter(Boolean);
      
      // Temizlenmiş varyantlar (İsmi veya fiyatı boş olanları sil)
      const cleanVariants = form.variants
        .filter(v => v.name.trim() !== '' && v.price !== '')
        .map(v => ({ name: v.name.trim(), price: Number(v.price) }));
      
      const docData = {
        isim: form.isim,
        aciklama: form.aciklama,
        kategori: form.kategori,
        fiyat: Number(form.fiyat) || 0, // Tek sabit fiyat
        variants: cleanVariants,        // Çoklu seçenek/fiyat listesi
        resim: images,
        video: videos,
        ozellikler: features,
      };

      if (editingProduct) {
        await updateDoc(doc(db, 'urunler', editingProduct.id), docData);
      } else {
        docData.created_at = new Date().toISOString();
        await addDoc(collection(db, 'urunler'), docData);
      }
      closeForm();
      await fetchProducts();
    } catch (err) {
      alert('Kaydetme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setForm({ isim: '', aciklama: '', kategori: '', fiyat: '', resim: '', video: '', ozellikler: '', variants: [] });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;
    try {
      await deleteDoc(doc(db, 'urunler', id));
      setHeroProductIds((prev) => prev.filter((pid) => pid !== id));
      await fetchProducts();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setForm({
      isim: product.name || '',
      aciklama: product.description || '',
      kategori: product.category || '',
      fiyat: String(product.price || ''),
      resim: (product.images || []).join('\n'),
      video: (product.video || []).join('\n'),
      ozellikler: (product.features || []).join('\n'),
      variants: product.variants || [], // Firestore'dan varyantları çek
    });
    setShowForm(true);
  };

  const toggleHeroProduct = (id) => {
    setSliderSaved(false);
    setHeroProductIds((prev) => {
      if (prev.includes(id)) return prev.filter((pid) => pid !== id);
      if (prev.length >= 5) { alert('Maksimum 5 ürün seçebilirsiniz.'); return prev; }
      return [...prev, id];
    });
  };

  const saveHeroSlides = async () => {
    setSliderSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'hero_slides'), { productIds: heroProductIds });
      setSliderSaved(true);
      setTimeout(() => setSliderSaved(false), 3000);
    } catch (err) {
      alert('Slider kaydetme hatası: ' + err.message);
    } finally {
      setSliderSaving(false);
    }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await setDoc(doc(db, 'site_config', 'settings'), siteSettings, { merge: true });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err) {
      alert('Ayar kaydetme hatası: ' + err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const formatPrice = (price) => price ? Number(price).toLocaleString('tr-TR') : '0';
  const allCategories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  const tabs = [
    { key: 'products', label: 'Ürünler', icon: Package },
    { key: 'slider', label: 'Slider', icon: Sliders },
    { key: 'categories', label: 'Kategoriler', icon: LayoutGrid },
    { key: 'settings', label: 'Ayarlar', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-100">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_ayz-modern-refresh/artifacts/v7g2xftl_bf9b2368-ab92-47b2-b299-567b89d2d406.jpeg" alt="AyzPlastik" className="h-8 w-auto" />
              <div className="flex items-center gap-1">
                <span className="font-semibold text-brand-dark text-sm">Admin Panel</span>
                <ChevronRight size={12} className="text-brand-dark/30" />
                <span className="text-brand-dark/50 text-xs">{tabs.find(t => t.key === activeTab)?.label}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a href="/" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-brand-dark/60 hover:text-brand-dark transition-colors"><Home size={16} /></a>
              <button onClick={() => signOut(auth)} className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-100 transition-colors"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Menu */}
      <div className="border-b border-slate-100 bg-white overflow-x-auto">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key ? 'border-brand-dark text-brand-dark' : 'border-transparent text-brand-dark/40 hover:text-brand-dark/60'
                }`}
              >
                <tab.icon size={16} /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-6">
        {/* --- PRODUCTS TAB --- */}
        {activeTab === 'products' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-brand-dark">Ürün Yönetimi</h2>
                <p className="text-xs text-brand-dark/40">{products.length} ürün listeleniyor</p>
              </div>
              <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-dark text-white text-sm font-medium hover:bg-brand-dark/90 transition-colors">
                <Plus size={16} /> Yeni Ürün
              </button>
            </div>

            {loading ? (
              <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-white animate-pulse" />)}</div>
            ) : (
              <div className="space-y-3">
                {products.map((product) => (
                  <motion.div key={product.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-100 hover:shadow-sm transition-shadow">
                    <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-50">
                      {product.images?.[0] ? (
                        <ProductImage src={product.images[0]} alt={product.name} name={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Image size={20} className="text-brand-dark/15" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-brand-dark text-sm truncate">{product.name || 'İsimsiz'}</h3>
                      <p className="text-xs text-brand-dark/40">
                        {product.category} • 
                        {/* Fiyat Gösterimi (Varyant varsa VARYANTLI yaz) */}
                        {product.variants && product.variants.length > 0 
                          ? ` ÇOKLU SEÇENEK (${product.variants.length})` 
                          : ` ${formatPrice(product.price)} TL`
                        }
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => openEdit(product)} className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center text-brand-dark/40 hover:text-brand-dark hover:bg-slate-100 transition-colors"><Edit3 size={14} /></button>
                      <button onClick={() => handleDelete(product.id)} className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-100 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* --- SLIDER TAB --- */}
        {activeTab === 'slider' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-brand-dark">Slider Yönetimi (Maks. 5)</h2>
              <button onClick={saveHeroSlides} disabled={sliderSaving} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${sliderSaved ? 'bg-green-500 text-white' : 'bg-brand-dark text-white hover:bg-brand-dark/90'} disabled:opacity-50`}>
                {sliderSaved ? <><Check size={16} /> Kaydedildi</> : sliderSaving ? 'Kaydediliyor...' : <><Save size={16} /> Slider'ı Kaydet</>}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {products.map((product) => {
                const isSelected = heroProductIds.includes(product.id);
                return (
                  <button key={product.id} onClick={() => toggleHeroProduct(product.id)} className={`text-left rounded-xl border-2 p-2 transition-all ${isSelected ? 'border-brand-dark bg-brand-dark/5' : 'border-slate-100 bg-white'}`}>
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-slate-50 mb-2">
                      <ProductImage src={product.images?.[0]} alt={product.name} name={product.name} className="w-full h-full object-cover" />
                      {isSelected && <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-dark flex items-center justify-center"><Check size={12} className="text-white" /></div>}
                    </div>
                    <p className="text-xs font-medium text-brand-dark truncate">{product.name}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* --- CATEGORIES TAB --- */}
        {activeTab === 'categories' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-brand-dark">Kategori Görselleri</h2>
                <p className="text-xs text-brand-dark/40">Ana sayfada görünecek kategori kapaklarını belirleyin.</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allCategories.map((cat) => (
                <div key={cat} className="p-4 rounded-xl bg-white border border-slate-100 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-slate-50 overflow-hidden relative border border-slate-100">
                      {categoryImages[cat] ? (
                        <img src={categoryImages[cat]} alt={cat} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                          <Image size={16} />
                          <span className="text-[8px] mt-1">Görsel Yok</span>
                        </div>
                      )}
                      
                      {categoryUploadingId === cat && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-bold text-brand-dark text-sm uppercase tracking-wide">{cat}</h3>
                      <p className="text-xs text-brand-dark/50 mt-0.5">{products.filter(p => p.category === cat).length} Ürün</p>
                    </div>
                  </div>

                  <label className="cursor-pointer w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-brand-dark/60 hover:text-brand-dark hover:bg-slate-100 transition-colors">
                    <UploadCloud size={16} />
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*" 
                      onChange={(e) => handleCategoryImgUpload(e, cat)} 
                      disabled={categoryUploadingId === cat}
                    />
                  </label>
                </div>
              ))}
              
              {allCategories.length === 0 && (
                <p className="text-brand-dark/40 text-sm w-full">Henüz oluşturulmuş kategori bulunmuyor.</p>
              )}
            </div>
          </div>
        )}

        {/* --- SETTINGS TAB --- */}
        {activeTab === 'settings' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-brand-dark">Site Ayarları</h2>
              <button onClick={saveSettings} disabled={settingsSaving} className="bg-brand-dark text-white px-4 py-2 rounded-full text-sm font-medium">
                {settingsSaved ? 'Kaydedildi' : settingsSaving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="p-5 rounded-xl bg-white border border-slate-100">
                <h3 className="font-semibold text-brand-dark text-sm mb-4 flex items-center gap-2">
                  <FileText size={16} /> Ürün Kataloğu
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-slate-500 uppercase">Katalog URL'si (PDF veya Görsel)</label>
                    <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-brand-dark/5 text-brand-dark text-[10px] font-bold hover:bg-brand-dark/10 transition-colors border border-brand-dark/10">
                      <Plus size={12} />
                      DOSYA YÜKLE
                      <input type="file" className="hidden" accept="application/pdf,image/*" onChange={handleCatalogUpload} disabled={catalogUploading} />
                    </label>
                  </div>
                  <input 
                    type="text" 
                    value={siteSettings.catalogUrl} 
                    onChange={(e) => setSiteSettings({...siteSettings, catalogUrl: e.target.value})} 
                    className="w-full h-11 px-4 border rounded-xl font-mono text-xs text-brand-dark/80" 
                    placeholder="https://..." 
                  />
                  {catalogUploading && <p className="text-[10px] text-brand-blue animate-pulse mt-1 font-bold">Katalog yükleniyor, lütfen bekleyin...</p>}
                  {siteSettings.catalogUrl && (
                    <div className="mt-2 text-xs">
                       <a href={siteSettings.catalogUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Mevcut Kataloğu Görüntüle</a>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 rounded-xl bg-white border border-slate-100 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Şirket Adı</label>
                  <input type="text" value={siteSettings.companyName} onChange={(e) => setSiteSettings({...siteSettings, companyName: e.target.value})} className="w-full h-11 px-4 border rounded-xl" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Slogan</label>
                  <input type="text" value={siteSettings.slogan} onChange={(e) => setSiteSettings({...siteSettings, slogan: e.target.value})} className="w-full h-11 px-4 border rounded-xl" />
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white border border-slate-100 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Telefon</label>
                  <input type="text" value={siteSettings.phone} onChange={(e) => setSiteSettings({...siteSettings, phone: e.target.value})} className="w-full h-11 px-4 border rounded-xl" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">E-posta</label>
                  <input type="text" value={siteSettings.email} onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})} className="w-full h-11 px-4 border rounded-xl" />
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white border border-slate-100">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Hakkımızda Metni</label>
                <textarea value={siteSettings.aboutText} onChange={(e) => setSiteSettings({...siteSettings, aboutText: e.target.value})} className="w-full p-4 border rounded-xl h-32" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ y: 50 }} animate={{ y: 0 }} className="bg-white w-full max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="font-bold text-lg">{editingProduct ? 'Ürünü Düzenle' : 'Yeni Ürün'}</h3>
                <button onClick={closeForm} className="p-2 hover:bg-slate-100 rounded-full"><X size={20} /></button>
              </div>
              
              <form onSubmit={handleSave} className="p-6 space-y-5">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Ürün Adı</label>
                  <input type="text" value={form.isim} onChange={(e) => setForm({...form, isim: e.target.value})} className="w-full h-11 px-4 border rounded-xl" required />
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Kategori</label>
                  <input type="text" value={form.kategori} onChange={(e) => setForm({...form, kategori: e.target.value})} className="w-full h-11 px-4 border rounded-xl" />
                </div>

                {/* --- FİYAT VE VARYANT ALANI --- */}
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-brand-dark uppercase">Sabit Fiyat (Opsiyonel)</label>
                    <p className="text-[10px] text-slate-500 mb-1">Eğer ürünün tek bir fiyatı varsa buraya yazın.</p>
                    <input type="number" value={form.fiyat} onChange={(e) => setForm({...form, fiyat: e.target.value})} className="w-full h-11 px-4 border rounded-xl bg-white" placeholder="0" />
                  </div>

                  <div className="h-px w-full bg-slate-200 my-2"></div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-bold text-brand-dark uppercase">Seçenekler / Varyantlar</label>
                      <button type="button" onClick={addVariant} className="text-[10px] font-bold bg-brand-dark text-white px-2 py-1 rounded flex items-center gap-1 hover:bg-brand-dark/80">
                        <Plus size={10} /> YENİ SEÇENEK
                      </button>
                    </div>
                    
                    {form.variants.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">"Işıklı", "Şarjlı" gibi farklı fiyat seçenekleri ekleyebilirsiniz.</p>
                    ) : (
                      <div className="space-y-2">
                        {form.variants.map((variant, index) => (
                          <div key={index} className="flex gap-2 items-center bg-white p-2 rounded-lg border border-slate-200">
                            <input 
                              type="text" 
                              placeholder="Örn: Şarjlı Işıklı Küre" 
                              value={variant.name} 
                              onChange={(e) => updateVariant(index, 'name', e.target.value)} 
                              className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-md"
                            />
                            <input 
                              type="number" 
                              placeholder="Fiyat (TL)" 
                              value={variant.price} 
                              onChange={(e) => updateVariant(index, 'price', e.target.value)} 
                              className="w-24 h-9 px-3 text-sm border border-slate-200 rounded-md"
                            />
                            <button type="button" onClick={() => removeVariant(index)} className="p-2 text-red-400 hover:text-red-600 bg-red-50 rounded-md">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* ------------------------------ */}

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-slate-500 uppercase">Görsel URL'leri</label>
                    <label className="cursor-pointer text-[10px] font-bold bg-slate-100 px-2 py-1 rounded flex items-center gap-1">
                      <Plus size={10} /> ÇOKLU DOSYA SEÇ
                      <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageUpload} disabled={saving} />
                    </label>
                  </div>
                  <textarea value={form.resim} onChange={(e) => setForm({...form, resim: e.target.value})} className="w-full p-4 border rounded-xl font-mono text-xs" rows={2} />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-slate-500 uppercase">Video URL'leri</label>
                    <label className="cursor-pointer text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-1 rounded flex items-center gap-1">
                      <Video size={10} /> ÇOKLU VİDEO SEÇ
                      <input type="file" className="hidden" accept="video/*" multiple onChange={handleVideoUpload} disabled={saving} />
                    </label>
                  </div>
                  <textarea value={form.video} onChange={(e) => setForm({...form, video: e.target.value})} className="w-full p-4 border rounded-xl font-mono text-xs" rows={2} />
                  {saving && <p className="text-[10px] text-blue-500 animate-pulse font-bold">Medya işleniyor, lütfen bekleyin...</p>}
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Özellikler (Her Satıra Bir Tane)</label>
                  <textarea value={form.ozellikler} onChange={(e) => setForm({...form, ozellikler: e.target.value})} className="w-full p-4 border rounded-xl text-sm" rows={3} />
                </div>

                <div className="flex gap-3 pt-4 sticky bottom-0 bg-white">
                  <button type="button" onClick={closeForm} className="flex-1 h-12 border rounded-full font-bold text-sm">İptal</button>
                  <button type="submit" disabled={saving} className="flex-1 h-12 bg-brand-dark text-white rounded-full font-bold text-sm disabled:opacity-50">
                    {saving ? 'İşleniyor...' : 'Ürünü Kaydet'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}